/**
 * Notification Store
 *
 * Global state management for toast notifications and error messages
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {
  Notification,
  NotificationAction,
} from '@/shared/components/errors/ErrorNotification'

interface NotificationState {
  notifications: Notification[]

  // Actions
  addNotification: (notification: Omit<Notification, 'id'>) => string
  removeNotification: (id: string) => void
  clearAllNotifications: () => void

  // Convenience methods
  showError: (error: Error | string, options?: NotificationOptions) => string
  showSuccess: (message: string, options?: NotificationOptions) => string
  showWarning: (message: string, options?: NotificationOptions) => string
  showInfo: (message: string, options?: NotificationOptions) => string
}

interface NotificationOptions {
  title?: string
  duration?: number
  persistent?: boolean
  actions?: NotificationAction[]
}

const DEFAULT_DURATIONS = {
  error: 8000,
  warning: 6000,
  info: 4000,
  success: 4000,
} as const

export const useNotificationStore = create<NotificationState>()(
  devtools(
    (set, get) => ({
      notifications: [],

      addNotification: notification => {
        const id = `notification_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
        const newNotification: Notification = {
          ...notification,
          id,
          duration:
            notification.duration ?? DEFAULT_DURATIONS[notification.type],
        }

        set(state => ({
          notifications: [...state.notifications, newNotification],
        }))

        return id
      },

      removeNotification: id => {
        set(state => ({
          notifications: state.notifications.filter(n => n.id !== id),
        }))
      },

      clearAllNotifications: () => {
        set({ notifications: [] })
      },

      showError: (error, options = {}) => {
        const message = typeof error === 'string' ? error : error.message

        return get().addNotification({
          type: 'error',
          message,
          title: options.title || 'Error',
          duration: options.duration,
          persistent: options.persistent,
          actions: options.actions,
        })
      },

      showSuccess: (message, options = {}) => {
        return get().addNotification({
          type: 'success',
          message,
          title: options.title,
          duration: options.duration,
          persistent: options.persistent,
          actions: options.actions,
        })
      },

      showWarning: (message, options = {}) => {
        return get().addNotification({
          type: 'warning',
          message,
          title: options.title || 'Warning',
          duration: options.duration,
          persistent: options.persistent,
          actions: options.actions,
        })
      },

      showInfo: (message, options = {}) => {
        return get().addNotification({
          type: 'info',
          message,
          title: options.title,
          duration: options.duration,
          persistent: options.persistent,
          actions: options.actions,
        })
      },
    }),
    { name: 'notification-store' }
  )
)

// Convenience hooks for common use cases
export const useErrorNotification = () => {
  const showError = useNotificationStore(state => state.showError)

  return {
    showError,
  }
}

export const useSuccessNotification = () => {
  const showSuccess = useNotificationStore(state => state.showSuccess)
  return { showSuccess }
}

export const useNotifications = () => {
  const notifications = useNotificationStore(state => state.notifications)
  const removeNotification = useNotificationStore(
    state => state.removeNotification
  )
  const clearAllNotifications = useNotificationStore(
    state => state.clearAllNotifications
  )

  return {
    notifications,
    removeNotification,
    clearAllNotifications,
  }
}
